import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import { FaTrashAlt } from 'react-icons/fa';
import { uploadPhysiotherapyPrescription } from '../../../services/api/cashless-physiotherapy-v2';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILES = 2;

const PhysiotherapyUploadPrescriptionModal = ({ isOpen, toggle, requestId, onUploadComplete }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);

    const handleClose = () => {
        setSelectedFiles([]);
        setIsLoading(false);
        toggle();
    };

    const validateFile = (file) => {
        if (!ALLOWED_TYPES.includes(file.type)) {
            toast.error('Invalid file type. Only JPEG, PNG, and PDF files are allowed.');
            return false;
        }
        if (file.size > MAX_SIZE) {
            toast.error('File too large. Maximum size is 5MB.');
            return false;
        }
        return true;
    };

    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (selectedFiles.length >= MAX_FILES) {
            toast.error(`You can upload a maximum of ${MAX_FILES} files.`);
            e.target.value = '';
            return;
        }
        if (validateFile(file)) {
            setSelectedFiles((prev) => [...prev, file]);
        }
        e.target.value = '';
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleProceed = async () => {
        if (selectedFiles.length === 0 || !requestId) return;
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('requestId', requestId);
            selectedFiles.forEach((file) => formData.append('prescriptionFiles', file));
            const response = await uploadPhysiotherapyPrescription(formData);
            if (response && response.message === 'success') {
                toast.success('Prescription uploaded successfully');
                onUploadComplete();
                handleClose();
            } else {
                toast.error('Failed to upload prescription');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Error while uploading prescription');
        } finally {
            setIsLoading(false);
        }
    };

    const getPreviewSrc = (file) =>
        file.type.startsWith('image/') ? URL.createObjectURL(file) : null;

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
            <ModalHeader
                style={{
                    backgroundColor: '#714FFF',
                    borderBottom: 'none',
                    padding: '12px 20px',
                    marginBottom: 0,
                    position: 'relative',
                }}
            >
                <span style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: '600' }}>
                    Upload Prescription
                </span>
                <button
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '14px',
                        background: 'none',
                        border: 'none',
                        color: '#FFFFFF',
                        fontSize: '22px',
                        lineHeight: 1,
                        cursor: 'pointer',
                        padding: 0,
                        opacity: 0.85,
                    }}
                >
                    ×
                </button>
            </ModalHeader>

            <ModalBody style={{ padding: '12px 16px 16px' }}>
                {/* File slots row */}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
                    {/* Render existing file cards */}
                    {selectedFiles.map((file, index) => {
                        const previewSrc = getPreviewSrc(file);
                        return (
                            <div
                                key={index}
                                style={{
                                    flex: 1,
                                    border: '1.5px dashed #AAAAAA',
                                    borderRadius: '8px',
                                    height: '130px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    backgroundColor: '#FAFAFA',
                                    overflow: 'hidden',
                                    padding: '8px',
                                }}
                            >
                                {previewSrc ? (
                                    <img
                                        src={previewSrc}
                                        alt={file.name}
                                        style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px' }}
                                    />
                                ) : (
                                    <div style={{
                                        backgroundColor: '#F0EBFF',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        color: '#714FFF',
                                        fontWeight: '600',
                                        fontSize: '11px',
                                        textAlign: 'center',
                                        wordBreak: 'break-word',
                                        maxWidth: '100%',
                                    }}>
                                        {file.name}
                                    </div>
                                )}
                                <button
                                    onClick={() => handleRemoveFile(index)}
                                    style={{
                                        position: 'absolute',
                                        top: '6px',
                                        right: '6px',
                                        background: '#FF4D4D',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                >
                                    <FaTrashAlt style={{ color: '#FFFFFF', fontSize: '9px' }} />
                                </button>
                            </div>
                        );
                    })}

                    {/* Show "+" slot if fewer than MAX_FILES selected */}
                    {selectedFiles.length < MAX_FILES && (
                        <div
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            style={{
                                flex: 1,
                                border: '1.5px dashed #AAAAAA',
                                borderRadius: '8px',
                                height: '130px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                backgroundColor: '#FAFAFA',
                            }}
                        >
                            <span style={{ fontSize: '28px', color: '#AAAAAA', lineHeight: 1 }}>+</span>
                        </div>
                    )}
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '14px', gap: '10px' }}>
                    <Button
                        onClick={handleClose}
                        style={{
                            flex: 1,
                            backgroundColor: '#FFFFFF',
                            color: '#714FFF',
                            fontWeight: '600',
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: '1px solid #714FFF',
                            fontSize: '14px',
                        }}
                    >
                        Leave
                    </Button>
                    <Button
                        onClick={handleProceed}
                        disabled={selectedFiles.length === 0 || isLoading}
                        style={{
                            flex: 1,
                            backgroundColor: selectedFiles.length === 0 || isLoading ? '#CCCCCC' : '#714FFF',
                            color: '#FFFFFF',
                            fontWeight: '600',
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '14px',
                        }}
                    >
                        {isLoading ? 'Uploading...' : 'Proceed'}
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default PhysiotherapyUploadPrescriptionModal;
